import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown, Option, Spinner } from "@fluentui/react-components";
import { useOrganization } from "../context/OrganizationContext";
import { useOrganizationStyles } from "./Organization.style";
import Header from "../components/Header";

const decorateEntry = (entry) => ({
  id: entry.id,
  name: entry.name,
  asDR: true,
  asDP: false,
  permissions: entry.permissions,
  facet: "DR",
  optionKey: `${entry.id}::DR`,
  displayName: `${entry.name} (DR)`,
});

// The Excel plugin only supports the data-requester (DR) experience, so the
// data-provider (DP) facet of any org is intentionally excluded — including
// the DP side of dual orgs. Only orgs the user has DR access to make it into
// the list, and the report-only dropdown therefore never has any entries.
export const splitOrganizationsByAccess = (organizations) => {
  const fullAccess = organizations
    .filter((org) => !!org.isDR)
    .map(decorateEntry)
    .sort((a, b) => a.name.localeCompare(b.name));

  return { fullAccessOrgs: fullAccess, reportOnlyOrgs: [] };
};

const OrganizationList = () => {
  const styles = useOrganizationStyles();
  const navigate = useNavigate();
  const {
    organizations,
    isOrganizationContextPending,
    organizationUnitSyncing,
    organizationUnitSyncingDisplayName,
    error,
    selectOrganization,
    fetchOrganizations,
  } = useOrganization();

  const [pendingOptionKey, setPendingOptionKey] = useState(null);
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const { fullAccessOrgs } = useMemo(
    () => splitOrganizationsByAccess(organizations),
    [organizations]
  );

  const handleSelectFullAccess = (_, data) => {
    setPendingOptionKey(data.optionValue || null);
  };

  const handleSwitch = async () => {
    const entry = fullAccessOrgs.find((e) => e.optionKey === pendingOptionKey);
    if (!entry) return;
    setIsSwitching(true);
    try {
      await selectOrganization(entry.id, entry.displayName);
      navigate("/dashboard");
    } finally {
      setIsSwitching(false);
    }
  };

  if (isOrganizationContextPending) {
    const pendingSubheading = organizationUnitSyncing
      ? organizationUnitSyncingDisplayName
        ? `Loading ${organizationUnitSyncingDisplayName}…`
        : "Loading organisation…"
      : "Getting your workspace ready…";

    return (
      <>
        <Header />
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.contentContainer}>
              <h1 className={styles.heading}>Select an Organization</h1>
              <p className={styles.subheading}>{pendingSubheading}</p>
              <div className={styles.pendingState}>
                <Spinner size="small" />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.contentContainer}>
              <h1 className={styles.heading}>Select an Organization</h1>
              <p className={styles.errorText}>{error}</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const fullAccessSelected =
    pendingOptionKey && fullAccessOrgs.find((o) => o.optionKey === pendingOptionKey);

  const canSwitch = !!pendingOptionKey && !isSwitching;
  const totalCount = fullAccessOrgs.length;

  return (
    <>
      <Header />
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.contentContainer}>
            <h1 className={styles.heading}>Select an Organization</h1>
            <p className={styles.subheading}>
              You have access to {totalCount} {totalCount === 1 ? "organization" : "organizations"}.
            </p>

            {fullAccessOrgs.length > 0 ? (
              <div className={styles.section}>
                <p className={styles.sectionLabel}>Your organizations</p>
                <Dropdown
                  className={styles.dropdown}
                  placeholder="Search"
                  value={fullAccessSelected ? fullAccessSelected.displayName : ""}
                  selectedOptions={fullAccessSelected ? [fullAccessSelected.optionKey] : []}
                  onOptionSelect={handleSelectFullAccess}
                  listbox={{ className: styles.dropdownListbox }}
                >
                  {fullAccessOrgs.map((entry) => (
                    <Option key={entry.optionKey} value={entry.optionKey} text={entry.displayName}>
                      {entry.displayName}
                    </Option>
                  ))}
                </Dropdown>
              </div>
            ) : (
              <div className={styles.emptyState}>No organizations available.</div>
            )}
          </div>

          <div className={styles.footer}>
            <Button className={styles.switchButton} onClick={handleSwitch} disabled={!canSwitch}>
              {isSwitching ? "Switching…" : "Switch"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrganizationList;
